<template>
    <div class="item" :class="{completed : isFilled}" @click="toggleDropdown" ref="paramContainerRef">
        <div class="itemContent">
            <div>
                <label :for=label>{{ labelFormatted }}</label>
                <button v-if="tooltip"
                        class="tooltip-trigger"
                        @click.stop.prevent="showTooltip = !showTooltip"
                        @blur="showTooltip = false"
                        tabindex="0"
                        role="button"
                >
                    <svg width="15px"
                         height="15px"
                         viewBox="0 0 22 22"
                         xmlns="http://www.w3.org/2000/svg"
                         xml:space="preserve"
                         style="fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:2;">
                        <g transform="matrix(1,0,0,1,-25616.2,-8406.49)">
                            <g id="Miscellaneous" transform="matrix(4.0118,0,0,3.78149,19548.7,0)">
                                <rect x="1250" y="0" width="1200" height="3000" style="fill:none;"/>
                                <g transform="matrix(0.249264,0,0,0.264446,1512.08,2222.73)">
                                    <path
                                            d="M12,22.75C6.07,22.75 1.25,17.93 1.25,12C1.25,6.07 6.07,1.25 12,1.25C17.93,1.25 22.75,6.07 22.75,12C22.75,17.93 17.93,22.75 12,22.75ZM12,2.75C6.9,2.75 2.75,6.9 2.75,12C2.75,17.1 6.9,21.25 12,21.25C17.1,21.25 21.25,17.1 21.25,12C21.25,6.9 17.1,2.75 12,2.75Z"
                                            style="fill:rgb(41,45,50);fill-rule:nonzero;"/>
                                </g>
                                <g transform="matrix(0.249264,0,0,0.264446,-4872.81,0)">
                                    <path
                                            d="M25626.9,8406.49C25632.9,8406.49 25637.7,8411.32 25637.7,8417.26C25637.7,8423.21 25632.9,8428.04 25626.9,8428.04C25621,8428.04 25616.2,8423.21 25616.2,8417.26C25616.2,8411.32 25621,8406.49 25626.9,8406.49ZM25626.9,8423.49C25626.4,8423.49 25625.9,8423.04 25625.9,8422.51C25625.9,8421.97 25626.4,8421.53 25626.9,8421.53C25627.4,8421.53 25627.9,8421.97 25627.9,8422.51C25627.9,8423.04 25627.5,8423.49 25626.9,8423.49ZM25626.9,8420.23C25626.4,8420.23 25625.9,8419.78 25625.9,8419.25L25625.9,8418.97C25625.9,8417.45 25627,8416.71 25627.5,8416.42C25627.9,8416.09 25628.1,8415.87 25628.1,8415.53C25628.1,8414.88 25627.6,8414.34 25626.9,8414.34C25626.3,8414.34 25625.7,8414.88 25625.7,8415.53C25625.7,8416.07 25625.3,8416.51 25624.7,8416.51C25624.2,8416.51 25623.8,8416.07 25623.8,8415.53C25623.8,8413.79 25625.2,8412.38 25626.9,8412.38C25628.6,8412.38 25630.1,8413.79 25630.1,8415.53C25630.1,8417.02 25629,8417.77 25628.6,8418.04C25628,8418.38 25627.9,8418.6 25627.9,8418.97L25627.9,8419.25C25627.9,8419.8 25627.4,8420.23 25626.9,8420.23Z"
                                            style="fill:rgb(0,3,51);"/>
                                </g>
                            </g>
                        </g>
                    </svg>
                </button>
            </div>

            <div class="custom-select" ref="customSelectRef">
                <div class="custom-selected-option" tabindex="0" role="button" ref="customSelectedRef">
                    {{ selectedOptionText ? selectedOptionText : placeholder }}
                </div>
                <Transition name="fade">
                    <div class="custom-select-options" v-if="isOpen" ref="customSelectOptionsRef">
                        <div class="custom-select-options-container">
                            <div v-for="(option, key) in options" :key="key" @click.stop="selectOption(option)" class="custom-select-option">
                                {{ option.label + (option.min ? ' (' + option.min + ' - ' + option.max + ')' : '') }}
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>

        </div>
        <div v-if="showTooltip" v-html="tooltip" class="tooltip-content"></div>
        <div class="errorContainer" v-if="formattedError">
            {{ formattedError }}
        </div>
    </div>
</template>

<script>
import {computed, defineComponent, ref, onMounted, onUnmounted} from "vue";

export default defineComponent({
    name: 'selectField',
    emits: ['update:modelValue', 'initiate-recalculation'],
    props: {
        label: {
            type: String,
            required: false,
            default: ""
        },
        id: {
            type: String,
            required: true,
            default: ""
        },
        modelValue: {
            type: String,
            required: false,
            default: ""
        },
        tooltip: {
            type: String,
            required: false,
        },
        options: {
            type: Array,
            required: true,
        },
        required: {
            type: Boolean,
            required: false,
            default: false
        },
        unit: {
            type: String,
            required: false,
            default: null
        },
        error: {
            type: Object,
            required: false
        },
        placeholder: {
            type: String,
            required: false
        }
    },
    setup(props, {emit}) {
        const customSelectedRef = ref(null)
        const customSelectOptionsRef = ref(null)
        const customSelectRef = ref(null)

        const paramContainerRef = ref(null)
        const inputValue = ref(props.modelValue);
        const isOpen = ref(false);
        const isFilled = computed(() => {
            return !!inputValue.value
        })
        const labelFormatted = computed(() => {
            if (props.required) {
                return props.label + ' *'
            } else {
                return props.label
            }
        })

        const toggleDropdown = () => {
            isOpen.value = !isOpen.value;
        };

        const selectOption = (option) => {
            inputValue.value = option.value;
            isOpen.value = false;
            emit("update:modelValue", option.value);
            emit("initiate-recalculation");
        };

        const formattedError = computed(() => {
            if (props.error) {
                return props.error.translated_msg.replace('%s', props.error.translated_param);
            } else {
                return null
            }
        })

        const selectedOptionText = computed(() => {
            const matchingOption = props.options.find(option => option.value === props.modelValue);
            return matchingOption ? matchingOption.label : '';
        });

        const showTooltip = ref(false)

        const handleClickOutside = (event) => {
            const isClickedOutside = paramContainerRef.value && !paramContainerRef.value.contains(event.target);

            if (isClickedOutside) {
                isOpen.value = false;
            }
        };

        onMounted(() => {
          document.addEventListener('click', handleClickOutside);
        });

        onUnmounted(() => {
          document.removeEventListener('click', handleClickOutside);
        });

        return {
            labelFormatted,
            isFilled,
            formattedError,
            showTooltip,
            isOpen,
            toggleDropdown,
            selectOption,
            inputValue,
            selectedOptionText,
            paramContainerRef,
            customSelectedRef,
            customSelectOptionsRef,
            customSelectRef
        }
    }
})
</script>

<style scoped lang="scss">
div.item {
  display: flex;
  flex-direction: column;
  padding: 10px 15px;
  border: solid 1px #e0e0e0;
  border-radius: 5px;
  cursor: pointer;
}

div.itemContent {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

div.itemContent > *:first-child {
  display: flex;
  align-items: center;
}

div.itemContent > select {
  all: unset;
  text-decoration: underline dotted 1px;
  text-underline-offset: 2px;
  text-align: right;
  cursor: pointer;
}

div.itemContent > div > label {
    cursor: pointer;
    margin-bottom: 0;
}

div.completed {
  background-color: #f7f7f7;
}

div.completed > div:first-of-type > div.itemContent > select {
  border-bottom: none;
}

div.errorContainer {
  font-size: 12px;
  margin: auto;
}

button.tooltip-trigger {
  /* remove default stying */
  background: none;
  border: none;
  padding: 0;
  /* end remove default stying */

  display: inline-block;
  cursor: pointer;
  margin-left: 5px;
  box-shadow: none;
}

button.tooltip-trigger > svg {
    vertical-align: middle;
}

div.tooltip-content {
  margin-top: 5px;
  color: #333333;
  font-size: 12px;
}

div.custom-selected-option {
  text-decoration: underline dotted 1px;
  text-underline-offset: 2px;
}

div.custom-select {
  cursor: pointer;
}

div.custom-select-option:hover {
  background-color: #f6f6f6;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(5px);
}

div.custom-select-options {
    background-color: #fff !important;
    cursor: pointer;
    box-shadow: 0 -3px 6px -2px rgba(50, 50, 93, .25);
    margin: 0;
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 3000 !important;
    white-space: nowrap;
    left: 0;
    right: 0;
    width: 100%;
    position: fixed;
    bottom: 0;
    border-radius: 30px 30px 0 0;
}

div.custom-select-option {
  padding: 20px;
  text-align: center;
  cursor: pointer;
  position: relative;
}

/* Create a divider without content */
div.custom-select-option::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0; /* Adjust the left position to control the divider width (e.g., 25% for 50% width) */
  width: 100%; /* Adjust the width of the divider (e.g., 50% for half width) */
  height: 1px; /* Adjust the height of the divider as needed */
  background-color: #f6f6f6; /* Adjust the color of the divider */
}

/* Hide the divider on the last item */
div.custom-select-option:last-child::after {
  display: none; /* Remove the border on the last item */
}

@media screen and (min-width: 551px) {
  div.custom-select-options {
    position: absolute;
    width: unset;
  }
}
</style>